import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, Button, Text, Flex } from '@radix-ui/themes';
import { useAuth } from '../../context/AuthContext';

const AlertPopup = () => {
  const { activeAlerts, clearAlert, playAlarm, alarmSettings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentAlert, setCurrentAlert] = useState(null);
  const [audio, setAudio] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  const roleId = localStorage.getItem('roleId');
  const currentUserId = parseInt(localStorage.getItem('userId'));

  // Alert types that should NOT show in this popup (handled elsewhere)
  const excludedAlertTypes = ['SOS', 'Reset', 'Confirmation', 'Hold', 'HoldExtension', 'SensorStatus'];

  // Pages where popup should NOT show
  const excludedPages = ['/my-patients', '/special-role-my-patients'];

  // Helper function to extract alert data (handles wrapped alerts)
  const extractAlertData = (alert) => {
    if (!alert || typeof alert !== 'object') return null;

    // Check if alert is wrapped (has numeric key like "0")
    if (alert["0"] && typeof alert["0"] === 'object') {
      return alert["0"];
    }

    // Direct alert structure
    return alert;
  };

  // Helper function to find the highest priority valid alert
  const getHighestPriorityAlert = () => {
    if (!activeAlerts || activeAlerts.length === 0) return null;

    console.log('AlertPopup - Processing alerts:', activeAlerts);

    // Don't show popup if on excluded pages
    const isOnExcludedPage = excludedPages.some(page => location.pathname.includes(page));
    if (isOnExcludedPage) {
      console.log('AlertPopup - On excluded page, not showing popup');
      return null;
    }

    // Filter and process alerts
    const validAlerts = [];

    activeAlerts.forEach((alert, index) => {
      const alertData = extractAlertData(alert);
      if (!alertData) {
        console.log(`AlertPopup - Invalid alert structure at index ${index}:`, alert);
        return;
      }

      const { alertType, payload } = alertData;
      
      console.log(`AlertPopup - Processing alert ${index}: Type=${alertType}`);

      // Skip excluded alert types
      if (excludedAlertTypes.includes(alertType)) {
        console.log(`AlertPopup - Skipping excluded alert type: ${alertType}`);
        return;
      }

      // Skip Reset alerts that are not for current user
      if (alertType === 'Reset' && payload && payload.userId !== currentUserId) {
        console.log(`AlertPopup - Skipping Reset alert for different user: ${payload.userId} vs ${currentUserId}`);
        return;
      }

      // Validate required fields for regular alarms
      if (!payload || !payload.masterAlarmId || payload.alarmPriority === undefined) {
        console.log(`AlertPopup - Alert missing required fields:`, alertData);
        return;
      }

      // Find alarm configuration
      const alarmConfig = alarmSettings.find(alarm => alarm.masterAlarmId === payload.masterAlarmId);
      if (!alarmConfig) {
        console.log(`AlertPopup - No alarm config found for masterAlarmId: ${payload.masterAlarmId}`);
        return;
      }

      // Create valid alert object
      const validAlert = {
        ...alertData,
        originalAlert: alert, // Keep reference to original for clearing
        alarmConfig,
        priority: payload.alarmPriority,
        bedId: payload.bedId,
        roomId: payload.roomId || 'Unknown'
      };

      validAlerts.push(validAlert);
    });

    // Sort by priority (lower number = higher priority) and return the highest priority
    const sortedAlerts = validAlerts.sort((a, b) => Number(a.priority) - Number(b.priority));
    
    console.log(`AlertPopup - Found ${validAlerts.length} valid alerts, highest priority:`, sortedAlerts[0]);
    
    return sortedAlerts.length > 0 ? sortedAlerts[0] : null;
  };

  // Get alarm color from alarmSettings
  const getAlarmColor = (masterAlarmId) => {
    const alarmConfig = alarmSettings.find(alarm => alarm.masterAlarmId === masterAlarmId);
    return alarmConfig ? alarmConfig.color : '#ff6b6b'; // Default red if no color found
  };

  // Stop current audio
  const stopCurrentAudio = () => {
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
        if (audio.parentNode) {
          audio.parentNode.removeChild(audio);
        }
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
      } catch (error) {
        console.error('Error stopping audio:', error);
      }
      setAudio(null);
      setAudioUrl(null);
    }
  };

  // Effect to handle alert changes and audio playback
  useEffect(() => {
    const highestPriorityAlert = getHighestPriorityAlert();
    
    if (highestPriorityAlert) {
      console.log('AlertPopup - Setting current alert:', highestPriorityAlert);
      setCurrentAlert(highestPriorityAlert);

      // Play alarm audio if masterAlarmId is present
      const masterAlarmId = highestPriorityAlert.payload?.masterAlarmId;
      if (masterAlarmId) {
        console.log('AlertPopup - Playing alarm audio for masterAlarmId:', masterAlarmId);
        playAlarm(masterAlarmId);
      }
    } else {
      console.log('AlertPopup - No valid alerts, clearing current alert');
      stopCurrentAudio();
      setCurrentAlert(null);
    }

    // Cleanup audio on unmount
    return () => {
      stopCurrentAudio();
    };
  }, [activeAlerts, location.pathname, playAlarm, alarmSettings]);

  // Handle dismiss action
  const handleDismiss = () => {
    if (currentAlert && currentAlert.originalAlert) {
      console.log('AlertPopup - Dismissing alert:', currentAlert);
      clearAlert(currentAlert.originalAlert.timestamp);
      stopCurrentAudio();
      setCurrentAlert(null);
    }
  };

  // Handle navigation to appropriate patients page
  const handleGoToPatients = () => {
    console.log('AlertPopup - Navigating to patients page, roleId:', roleId);
    
    stopCurrentAudio();
    setCurrentAlert(null);
    
    // Navigate based on roleId
    if (roleId === '3' || roleId === '4') {
      navigate('/my-patients');
    } else {
      navigate('/special-role-my-patients');
    }
  };

  // Don't render if no valid alert
  if (!currentAlert) {
    return null;
  }

  // Extract alert details
  const alarmName = currentAlert.payload?.alarmName || 
                   currentAlert.alarmConfig?.alarmName || 
                   currentAlert.alertType || 
                   'Unknown Alert';
  const roomNumber = currentAlert.roomId || 'Unknown';
  const bedId = currentAlert.bedId || 'Unknown';
  const masterAlarmId = currentAlert.payload?.masterAlarmId;
  const alarmColor = getAlarmColor(masterAlarmId);

  console.log('AlertPopup - Rendering popup:', {
    alarmName,
    roomNumber,
    bedId,
    alarmColor,
    masterAlarmId
  });

  return (
    <Dialog.Root open={true}>
      <Dialog.Content 
        className="max-w-md p-0 rounded-2xl shadow-lg overflow-hidden animate-pulse-enhanced"
        style={{ backgroundColor: alarmColor }}
      >
        <style>
          {`
            @keyframes pulse-enhanced {
              0% {
                box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7), 0 0 10px 2px rgba(255, 0, 0, 0.5);
              }
              50% {
                box-shadow: 0 0 0 12px rgba(255, 0, 0, 0.2), 0 0 20px 4px rgba(255, 0, 0, 0.8);
              }
              100% {
                box-shadow: 0 0 0 0 rgba(255, 0, 0, 0), 0 0 10px 2px rgba(255, 0, 0, 0.5);
              }
            }
            .animate-pulse-enhanced {
              animation: pulse-enhanced 1.2s ease-in-out infinite;
            }
          `}
        </style>
        
        <Flex 
          direction="column" 
          justify="center" 
          align="center" 
          className="px-6 py-8"
        >
          <Text size="6" weight="bold" className="text-white mb-2 text-center">
            Alert
          </Text>
          
          <Text size="5" weight="bold" className="text-white mb-3 text-center">
            {alarmName}
          </Text>
          
          <Flex gap="3" justify="center" className="mt-2">
            <Button
              size="3"
              variant="soft"
              color="red"
              onClick={handleDismiss}
              className="bg-red-500/20 hover:bg-red-500/30 text-white border-red-500/30 font-semibold"
            >
              Dismiss
            </Button>
            
            <Button
              size="3"
              variant="soft"
              onClick={handleGoToPatients}
              className="bg-white/20 hover:bg-white/30 text-white border-white/30 font-semibold"
            >
              Go to My Patients
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AlertPopup;